import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW2CInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form W-2c Instructions: Guide to Correct Form W-2"
          description="Learn how to correct Form W-2 errors with Form W-2c. Follow step-by-step instructions for e-filing, and choose TaxZerone for an easy Form W-2c e-filing."
          keywords=""
          canonical="resources/information-returns/form-w-2c-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form W-2c</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w-2c-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2c at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        {' '}
                        Form
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          W-2c, Corrected Wage and Tax Statement{' '}
                        </strong>{' '}
                        is used to correct errors reported on a previously filed{' '}
                        <strong style={{ fontWeight: 600 }}> Form W-2</strong>.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Employers use this form to amend incorrect wage, tax
                        withholding, employee information, and state or local
                        tax information that was originally reported to
                        employees, the Social Security Administration (SSA), and
                        tax agencies.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions including
                        employer information, employee information, Tax Year (
                        <strong style={{ fontWeight: 600 }}>Box c</strong>),
                        Correct SSN (
                        <strong style={{ fontWeight: 600 }}>Box d</strong>),
                        Employee Name/SSN Corrections (
                        <strong style={{ fontWeight: 600 }}>Boxes e-h</strong>),
                        Employee Address (
                        <strong style={{ fontWeight: 600 }}>Box i</strong>),{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 1-14</strong>{' '}
                        (Federal Wage, Tax, and Compensation Corrections), and
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Boxes 15-20
                        </strong>{' '}
                        (State and Local Information Corrections).
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form W-2c?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form W-2c, the &quot;Corrected Wage and Tax Statement,&quot;
                  is used to fix errors on previously submitted{' '}
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form W-2
                  </Link>
                  . Employers can use Form W-2c to correct inaccuracies in the
                  wage and tax information they reported to employees and the
                  IRS.
                </Typography>

                {/* <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Rent payments</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Royalties</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Prizes and awards</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Payments to attorneys</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments for services to unincorporated businesses
                    </ListItemText>
                  </ListItem>
                </List> */}

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Note:</b> Make sure your employees fill out and submit Form
                  W-4 so you have the necessary information to complete the W-2
                  accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the due date for filing Form W-2c?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  There is no specific deadline for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form W-2c
                  </Link>
                  . However, it is important to correct any errors in your
                  returns as soon as they are identified. Additionally, promptly
                  send corrected copies to your recipients.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What information can be corrected on Form W-2c?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  You can correct the following information reported on Form
                  W-2:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>Tax year</li>
                  <li style={{ marginBottom: 12 }}>
                    Employer Identification Number (EIN)
                  </li>
                  <li style={{ marginBottom: 12 }}>Employee name and SSN</li>
                  <li style={{ marginBottom: 12 }}>
                    Employee earnings and tax withholdings:
                  </li>
                  <ul>
                    <li style={{ marginBottom: 12 }}>
                      Wages, tips, and other compensation
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Federal income tax withheld
                    </li>
                    <li style={{ marginBottom: 12 }}>Social security wages</li>
                    <li style={{ marginBottom: 12 }}>
                      Medicare tax withheld and Medicare wages and tips
                    </li>
                    <li style={{ marginBottom: 12 }}>Allocated tips</li>
                    <li style={{ marginBottom: 12 }}>
                      State and local information
                    </li>{' '}
                  </ul>
                </ul>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2">
                        Reported any of the information mentioned above
                        incorrectly on Form W-2? E-file Form W-2c with TaxZerone
                        now.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2cinstructions`}
                      >
                        E-file Form W-2c
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Instructions to file Form W-2c
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form W-2c.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Employer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-w-2c-employer-details.webp" />
                  <source srcSet="/assets/images/form-w-2c-employer-details.png" />
                  <img
                    src="/assets/images/form-w-2c-employer-details.png"
                    loading="lazy"
                    alt="Employer details"
                    width={513}
                    height={294}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the employer&apos;s name, complete address, and EIN.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Employee details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/form-w-2c-employer-correction.webp" />
                  <source srcSet="/assets/images/form-w-2c-employer-correction.png" />
                  <img
                    src="/assets/images/form-w-2c-employer-correction.png"
                    loading="lazy"
                    alt="Employee details"
                    width={500}
                    height={361}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Box c: Enter the tax year for which the form is corrected
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box d: Enter the employee’s correct SSN
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box e: Check this box only if you are correcting the
                    employee’s name or SSN
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box f: Enter the SSN that you have reported in the
                    previously filed return
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box g: Enter the employee name that you have reported in the
                    previously filed return
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box h: Enter the employee’s correct name
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box i: Enter the employee’s address and ZIP code
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 1 to 14
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-w-2c-boxes-1-to-14.webp" />
                  <source srcSet="/assets/images/form-w-2c-boxes-1-to-14.png" />
                  <img
                    src="/assets/images/form-w-2c-boxes-1-to-14.png"
                    loading="lazy"
                    alt="Boxes 1 to 14"
                    width={764}
                    height={340}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the previously reported amount and the correct amount in
                  the respective boxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are not sure how to fill out previously reported amount
                  boxes, refer to our{' '}
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#2020ff' }}
                  >
                    Form W-2 instructions
                  </Link>
                  .
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 15 to 20
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-w-2c-boxes-15-to-20.webp" />
                  <source srcSet="/assets/images/form-w-2c-boxes-15-to-20.png" />
                  <img
                    src="/assets/images/form-w-2c-boxes-15-to-20.png"
                    loading="lazy"
                    alt="Boxes 15 to 20"
                    width={762}
                    height={306}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you want to correct any details related to state and
                  locality, report those in the respective boxes.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form W-2c?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file W-2c Form
                  </Link>{' '}
                  through online e-filing and traditional paper filing.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form W-2c E-filing (recommended)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-filing your W-2c
                  </Link>{' '}
                  for faster processing. Plus, you&apos;ll get instant
                  notifications when they&apos;re processed. With TaxZerone, you
                  can fill out your W-2c in just a few clicks, review and submit
                  it, and share recipient copies easily.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Benefits of e-filing:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Faster processing and reduced errors
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Simpler, more streamlined experience
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Instant notifications upon form processing
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Secure transmission of sensitive data
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Choose e-filing for a smooth and efficient W-2c filing
                  experience.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form W-2c, here are the steps you
                  need to follow:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print Form W-2c from the{' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/fw2c.pdf"
                      target="_blank"
                      sx={{ color: '#2020ff' }}
                    >
                      IRS website
                    </Link>
                    .
                  </li>
                  <li style={{ marginBottom: 16 }}>Fill out:</li>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Recipient details (name, address, SSN, compensation)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Your business information
                    </li>
                  </ul>
                  <li style={{ marginBottom: 16 }}>
                    Mail the completed form to SSA
                  </li>
                  <li>Send a copy to each recipient</li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      display: 'flex',
                    }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important notes:
                  </Typography>
                  <ul style={{ marginBottom: 0 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail early to ensure timely arrival
                    </li>
                    <li>
                      Always provide copies to recipients for their tax
                      reporting
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form W-2c mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you have chosen to file Form W-2c by paper, send the
                  completed form to the mailing address below:
                </Typography>
                <Box
                  component="div"
                  sx={{
                    background:
                      'linear-gradient(180deg, rgba(11,118,239,0.03), rgba(11,118,239,0.3))',
                    display: 'inline-block',
                    p: ' .35rem 1.5rem',
                    borderRadius: 2,
                    alignItems: 'center',
                    mb: 5,
                    boxShadow:
                      ' rgba(113, 172, 249, 0.4) 5px 5px, rgba(67, 224, 255, 0.3) 10px 10px, rgba(46, 162, 240, 0.2) 15px 15px, rgba(46, 149, 240, 0.1) 20px 20px, rgba(46, 120, 240, 0.05) 25px 25px',
                  }}
                >
                  <Typography
                    component="div"
                    variant="body1"
                    sx={{ fontWeight: 600, mb: 0.8 }}
                  >
                    {' '}
                    <MailOutlineOutlinedIcon
                      sx={{
                        position: 'relative',
                        ml: 0,
                        mr: 0.5,
                        mb: -1,
                        fontSize: 28,
                      }}
                    />{' '}
                    Mailing Address:
                  </Typography>
                  <Box
                    component="div"
                    sx={{ textAlign: 'left', fontSize: '15px important' }}
                  >
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      Social Security Administration,
                      <br /> Direct Operations Center,
                      <br /> P.O. Box 3333,
                      <br /> Wilkes-Barre, PA 18767-3333
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form W-2c with TaxZerone?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Follow the steps below to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form W-2c
                  </Link>{' '}
                  using TaxZerone.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1:</b> Fill out Form W-2c with previously reported and
                  correct information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2:</b> Review & transmit the return
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3:</b> Send the recipient copy
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Why choose TaxZerone to e-file Form W-2c?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  When you choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form W-2c
                  </Link>{' '}
                  with TaxZerone, you can:
                </Typography>
                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Catch any errors before you submit.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Upload multiple forms and finish in a flash.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Share recipient copies by email instantly
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Share recipient copies in a secure
                      portal—ZeroneVault—where employees can download them
                      anytime, anywhere.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Enjoy the industry&apos;s best price, starting at just
                      <span style={{ fontWeight: 600 }}> $0.59 per</span> form.
                    </ListItemText>
                  </ListItem>
                </List>
                <Box sx={{ textAlign: 'center', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 1,
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    className="btn-primary"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2cinstructions`}
                  >
                    E-file Form W-2c
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Get these benefits by e-filing your
              <br />
              Form W-2 with TaxZerone
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2instructions`}
              >
                E-file Form W-2
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default FormW2CInstructions;
